// =====================================================
// 项目管理组件 - 保存、加载、管理项目
// Apple Design 风格重构
// =====================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Save,
  FolderOpen,
  Plus,
  Trash2,
  Copy,
  FileText,
  Clock,
  MoreVertical,
  Loader2,
  AlertCircle,
  Sparkles,
  LayoutGrid,
} from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';

// =====================================================
// 苹果风格 Hooks
// =====================================================

/** 500ms 延迟显示 loading - 避免短暂加载闪烁 */
function useDelayedLoading(isLoading: boolean, delay = 500) {
  const [showLoading, setShowLoading] = useState(false);
  
  useEffect(() => {
    if (!isLoading) {
      setShowLoading(false);
      return;
    }
    const timer = setTimeout(() => setShowLoading(true), delay);
    return () => clearTimeout(timer);
  }, [isLoading, delay]);
  
  return showLoading;
}

/** 渐进动画时长计算 */
function getEasedDelay(index: number, baseDelay = 50) {
  return index * baseDelay;
}
import { useCanvasStore } from '../stores/canvas-store';
import {
  fetchProjects,
  createProject,
  updateProject,
  deleteProject,
  duplicateProject,
} from '../services/project-service';
import type { CanvasProject, CanvasData } from '../types';

// =====================================================
// 类型定义
// =====================================================

interface ProjectManagerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'save' | 'load' | 'new';
  onProjectLoaded?: (project: CanvasProject) => void;
  onProjectSaved?: (project: CanvasProject) => void;
  onProjectCreated?: (name: string, description?: string) => void;
}

// =====================================================
// 辅助组件
// =====================================================

/** 苹果风格项目卡片 */
function ProjectCard({
  project,
  onSelect,
  onDelete,
  onDuplicate,
  isLoading,
  isSelected,
  index = 0,
}: {
  project: CanvasProject;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  isLoading: boolean;
  isSelected?: boolean;
  index?: number;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return '刚刚';
    if (diffMins < 60) return `${diffMins} 分钟前`;
    if (diffHours < 24) return `${diffHours} 小时前`;
    if (diffDays < 7) return `${diffDays} 天前`;
    
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric',
    });
  };
  
  const cellCount = project.canvas_data?.cells?.length || 0;
  const filledCount = project.canvas_data?.cells?.filter(
    c => c.product_id || c.custom_image
  ).length || 0;
  
  // 获取布局显示文本
  const getLayoutDisplay = () => {
    const layout = project.layout_type.replace('grid_', '');
    if (layout === 'custom') {
      const settings = project.canvas_settings as { customRows?: number; customCols?: number } | undefined;
      return `${settings?.customRows || 3}×${settings?.customCols || 3}`;
    }
    return layout.toUpperCase();
  };
  
  return (
    <div
      className={`
        relative group rounded-2xl overflow-hidden cursor-pointer
        transition-all duration-300 ease-out
        ${isSelected 
          ? 'ring-2 ring-blue-500 ring-offset-2 shadow-lg scale-[1.02]' 
          : 'hover:shadow-xl hover:scale-[1.02]'
        }
        ${isLoading ? 'opacity-50 pointer-events-none' : ''}
      `}
      style={{
        animation: `fadeSlideUp 0.4s ease-out ${getEasedDelay(index)}ms backwards`,
      }}
      onClick={onSelect}
    >
      {/* 项目预览 - 渐变背景 */}
      <div className="relative h-28 bg-gradient-to-br from-gray-100 via-gray-50 to-white flex items-center justify-center overflow-hidden">
        {/* 网格预览图示 */}
        <div className="absolute inset-4 opacity-20">
          <div 
            className="w-full h-full grid gap-1"
            style={{
              gridTemplateColumns: `repeat(${Math.min(4, cellCount)}, 1fr)`,
              gridTemplateRows: `repeat(${Math.min(4, Math.ceil(cellCount / 4))}, 1fr)`,
            }}
          >
            {Array.from({ length: Math.min(16, cellCount) }).map((_, i) => (
              <div 
                key={i} 
                className={`rounded-sm ${i < filledCount ? 'bg-blue-400' : 'bg-gray-300'}`}
              />
            ))}
          </div>
        </div>
        
        {/* 布局标签 */}
        <div className="relative z-10 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm">
          <span className="text-sm font-semibold text-gray-700">
            {getLayoutDisplay()}
          </span>
        </div>
        
        {/* 状态标签 */}
        {project.is_draft && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded-full">
            草稿
          </span>
        )}
        
        {/* 操作菜单按钮 */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm hover:bg-white transition-colors"
          >
            <MoreVertical className="w-3.5 h-3.5 text-gray-600" />
          </button>
          
          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                }}
              />
              <div className="absolute right-0 top-full mt-1 bg-white/95 backdrop-blur-xl rounded-xl shadow-xl border border-gray-200/50 py-1 z-20 min-w-[120px] overflow-hidden">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate();
                    setShowMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 transition-colors"
                >
                  <Copy className="w-4 h-4 text-gray-500" />
                  <span>复制项目</span>
                </button>
                <div className="h-px bg-gray-200 mx-2" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    setShowDeleteConfirm(true);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>删除</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* 项目信息 */}
      <div className="p-4 bg-white">
        <h3 className="font-semibold text-gray-900 truncate text-[15px] leading-tight">
          {project.name}
        </h3>
        <p className="text-[13px] text-gray-500 truncate mt-1 leading-tight">
          {project.description || '暂无描述'}
        </p>
        
        {/* 元数据 */}
        <div className="flex items-center gap-3 mt-3 text-[11px] text-gray-400">
          <span className="flex items-center gap-1">
            <LayoutGrid className="w-3 h-3" />
            {filledCount}/{cellCount} 已填充
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDate(project.updated_at)}
          </span>
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="删除项目"
        message={`确定删除项目 "${project.name}" 吗？此操作不可撤销。`}
        variant="danger"
        confirmText="删除"
        onConfirm={() => { setShowDeleteConfirm(false); onDelete(); }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}

// =====================================================
// 主组件
// =====================================================

export function ProjectManager({
  isOpen,
  onClose,
  mode,
  onProjectLoaded,
  onProjectSaved,
  onProjectCreated,
}: ProjectManagerProps) {
  const [projects, setProjects] = useState<CanvasProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  
  const {
    layoutType,
    styleType,
    canvasData,
    customRows,
    customCols,
    currentProjectId,
    currentProjectName,
    setProjectInfo,
    loadProject,
    markAsSaved,
    clearHistory,
  } = useCanvasStore();
  
  // 加载项目列表
  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchProjects();
      setProjects(data);
    } catch (err) {
      setError('加载项目列表失败');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Effect 1: 加载项目列表（仅在非 new 模式且弹窗打开时）
  useEffect(() => {
    if (isOpen && mode !== 'new') {
      loadProjects();
    }
  }, [isOpen, mode, loadProjects]);
  
  // Effect 2: 初始化表单值（仅在弹窗打开时执行一次）
  useEffect(() => {
    if (!isOpen) return;
    
    // 根据模式设置默认值
    if (mode === 'save') {
      // 优先使用当前项目名称
      setProjectName(currentProjectName || `项目_${new Date().toLocaleDateString('zh-CN')}`);
      // 描述默认为空，用户可以自己填写
      setProjectDescription('');
      // 重置选中状态
      setSelectedProjectId(null);
    } else if (mode === 'new') {
      setProjectName(`项目_${new Date().toLocaleDateString('zh-CN')}`);
      setProjectDescription('');
    }
    // 清除错误状态
    setError(null);
  }, [isOpen, mode, currentProjectName]); // 注意：不依赖 projects，避免加载完成后重新初始化
  
  // Effect 3: 保存模式下，如果当前项目ID变化，尝试加载该项目的描述
  useEffect(() => {
    if (!isOpen || mode !== 'save' || !currentProjectId || projects.length === 0) return;
    
    const currentProject = projects.find(p => p.id === currentProjectId);
    if (currentProject) {
      setProjectName(currentProject.name);
      setProjectDescription(currentProject.description || '');
    }
  }, [currentProjectId]); // 仅在 currentProjectId 变化时执行，而不是每次 projects 变化
  
  // 保存项目
  const handleSave = async () => {
    if (!projectName.trim()) {
      setError('请输入项目名称');
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      const projectData = {
        name: projectName.trim(),
        description: projectDescription.trim() || undefined,
        layoutType,
        styleType,
        canvasData: canvasData as CanvasData,
        canvasSettings: { customRows, customCols },
        isDraft: true,
      };
      
      let savedProject: CanvasProject;
      
      if (currentProjectId && !selectedProjectId) {
        // 更新现有项目
        savedProject = await updateProject(currentProjectId, projectData);
      } else if (selectedProjectId) {
        // 覆盖选中的项目
        savedProject = await updateProject(selectedProjectId, projectData);
      } else {
        // 创建新项目
        savedProject = await createProject(projectData);
      }
      
      // 更新 store 状态
      setProjectInfo(savedProject.id, savedProject.name);
      markAsSaved();
      
      onProjectSaved?.(savedProject);
      onClose();
    } catch (err) {
      setError('保存失败，请重试');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };
  
  // 加载项目
  const handleLoad = async (project: CanvasProject) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // 使用 loadProject 方法加载整个项目
      loadProject(project);
      clearHistory(); // 清空历史记录
      
      onProjectLoaded?.(project);
      onClose();
    } catch (err) {
      setError('加载失败，请重试');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 删除项目
  const handleDelete = async (id: string) => {
    try {
      await deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      if (currentProjectId === id) {
        setProjectInfo(undefined, '未命名项目');
      }
    } catch (err) {
      setError('删除失败');
      console.error(err);
    }
  };
  
  // 复制项目
  const handleDuplicate = async (id: string) => {
    try {
      const newProject = await duplicateProject(id);
      setProjects(prev => [newProject, ...prev]);
    } catch (err) {
      setError('复制失败');
      console.error(err);
    }
  };
  
  // 新建项目
  const handleCreate = () => {
    if (!projectName.trim()) {
      setError('请输入项目名称');
      return;
    }
    
    onProjectCreated?.(projectName.trim(), projectDescription.trim() || undefined);
    onClose();
  };
  
  // 使用延迟加载显示 - 必须在条件返回之前调用（React Hooks 规则）
  const showLoadingSpinner = useDelayedLoading(isLoading);
  
  // 获取标题
  const getTitle = () => {
    switch (mode) {
      case 'new': return '新建项目';
      case 'save': return '保存项目';
      case 'load': return '项目列表';
      default: return '项目管理';
    }
  };
  
  // 获取图标
  const getTitleIcon = () => {
    switch (mode) {
      case 'new': return <Plus className="w-5 h-5 text-green-600" />;
      case 'save': return <Save className="w-5 h-5 text-blue-600" />;
      case 'load': return <FolderOpen className="w-5 h-5 text-blue-600" />;
      default: return <FolderOpen className="w-5 h-5 text-blue-600" />;
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      {/* 苹果风格动画 CSS */}
      <style>{`
        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes modalEnter {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-shimmer {
          background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
      `}</style>
      
      <div 
        className="bg-white/95 backdrop-blur-xl rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl border border-gray-200/50"
        style={{ animation: 'modalEnter 0.3s ease-out' }}
      >
        {/* Header - 苹果风格 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/80">
          <div className="flex items-center gap-3">
            <div className={`
              w-9 h-9 rounded-xl flex items-center justify-center
              ${mode === 'new' ? 'bg-green-100' : mode === 'save' ? 'bg-blue-100' : 'bg-gray-100'}
            `}>
              {getTitleIcon()}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {getTitle()}
              </h2>
              {mode === 'load' && projects.length > 0 && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {projects.length} 个项目
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors group"
          >
            <X className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
          </button>
        </div>
        
        {/* Error - 苹果风格 */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 rounded-xl flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-4 h-4 text-red-600" />
            </div>
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* 新建/保存模式 - 项目信息表单 */}
          {(mode === 'save' || mode === 'new') && (
            <div className="mb-8 space-y-5">
              <div>
                <label className="block text-[13px] font-medium text-gray-600 mb-2">
                  项目名称
                </label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="输入项目名称"
                  autoFocus
                  className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl text-[15px] placeholder-gray-400
                    focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-gray-600 mb-2">
                  项目描述 <span className="text-gray-400 font-normal">（可选）</span>
                </label>
                <textarea
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  placeholder="简要描述此项目的用途..."
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl text-[15px] placeholder-gray-400 resize-none
                    focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200"
                />
              </div>
              
              {mode === 'new' && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
                  <Sparkles className="w-5 h-5 text-blue-500" />
                  <p className="text-[13px] text-blue-700">
                    新项目将使用默认的 3×3 布局，您可以在创建后随时更改
                  </p>
                </div>
              )}
              
              {mode === 'save' && currentProjectId && (
                <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  <p className="text-[13px] text-amber-700">
                    当前正在编辑的项目将被更新，或选择下方项目进行覆盖保存
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* 项目列表 - 仅在 save 和 load 模式显示 */}
          {mode !== 'new' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-wide">
                  {mode === 'save' ? '选择覆盖（可选）' : '我的项目'}
                </h3>
                {showLoadingSpinner && (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    <span className="text-xs text-gray-400">加载中...</span>
                  </div>
                )}
              </div>
              
              {projects.length === 0 && !isLoading ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
                    <FolderOpen className="w-8 h-8 text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-medium">暂无保存的项目</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {mode === 'load' ? '点击"新建项目"开始创作' : '保存您的第一个项目'}
                  </p>
                </div>
              ) : isLoading && projects.length === 0 ? (
                // 骨架屏加载状态
                <div className="grid grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-2xl overflow-hidden">
                      <div className="h-28 skeleton-shimmer" />
                      <div className="p-4 bg-white space-y-3">
                        <div className="h-4 w-3/4 rounded skeleton-shimmer" />
                        <div className="h-3 w-1/2 rounded skeleton-shimmer" />
                        <div className="h-3 w-2/3 rounded skeleton-shimmer" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {projects.map((project, index) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      index={index}
                      isSelected={selectedProjectId === project.id}
                      onSelect={() => {
                        if (mode === 'load') {
                          handleLoad(project);
                        } else {
                          setSelectedProjectId(
                            selectedProjectId === project.id ? null : project.id
                          );
                        }
                      }}
                      onDelete={() => handleDelete(project.id)}
                      onDuplicate={() => handleDuplicate(project.id)}
                      isLoading={isLoading}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer - 苹果风格 */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200/80 bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-[15px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          >
            取消
          </button>
          
          {mode === 'new' && (
            <button
              onClick={handleCreate}
              disabled={!projectName.trim()}
              className="px-5 py-2.5 bg-gradient-to-b from-green-500 to-green-600 text-white text-[15px] font-semibold 
                rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-green-500 disabled:hover:to-green-600
                shadow-sm hover:shadow flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              创建项目
            </button>
          )}
          
          {mode === 'save' && (
            <button
              onClick={handleSave}
              disabled={isSaving || !projectName.trim()}
              className="px-5 py-2.5 bg-gradient-to-b from-blue-500 to-blue-600 text-white text-[15px] font-semibold 
                rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-500 disabled:hover:to-blue-600
                shadow-sm hover:shadow flex items-center gap-2"
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              <Save className="w-4 h-4" />
              {currentProjectId && !selectedProjectId ? '更新项目' : '保存项目'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectManager;
